import { useState, useEffect, useCallback } from 'react'
import { useDispatch } from 'react-redux'
import { pubsubActions } from '@redux'
import pubSubEventHandler from './eventHandler'
import { logger, storageHelpers } from '@utils'
import { customToast, CONNECTION_STATUS } from '@common'
import ErrorTrackingAPI from '../ErrorTrackingAPI'

const WEB_PUBSUB_PROTOCOLS = ['json.webpubsub.azure.v1']

// Module-level flag to prevent duplicate connection success toasts
// This ensures only one toast is shown even if _handleOpen is called multiple times
let lastConnectionToastTime = 0
const CONNECTION_TOAST_DEBOUNCE_MS = 2000

class PubSubService {
    constructor() {
        this.socket = null
        this.connectionStatus = CONNECTION_STATUS.DISCONNECTED
        this.connectionError = null
        this.listeners = []
        this.reconnectAttempts = 0
        this.maxReconnectAttempts = 5
        this.reconnectInterval = 3000
        this.negotiateEndpoint = process.env.REACT_APP_NEGOTIATE_ENDPOINT
        this.connectionStatusListeners = []
        this.reconnectTimeout = null
        // Flag to control automatic reconnection (useful for testing)
        this.isAutoReconnectEnabled = true
        // Flag to prevent duplicate connection attempts (race condition protection)
        this._isConnecting = false
        // Connect attempt ID to prevent race condition: disconnect during negotiate
        this._connectAttemptId = 0

        // Track joined groups locally
        this._groups = new Set()
        this._groupStatusListeners = []
        // Track "desired" groups (what the app intends to be in). Rejoin these after reconnect
        this._desiredGroups = new Set()
    }

    getNegotiateEndpoint() {
        return this.negotiateEndpoint
    }
    setNegotiateEndpoint(endpoint) {
        this.negotiateEndpoint = endpoint
    }
    getConnectionStatus() {
        return this.connectionStatus
    }
    getConnectionError() {
        return this.connectionError
    }

    addConnectionStatusListener(listener) {
        this.connectionStatusListeners.push(listener)
        return () => {
            this.connectionStatusListeners = this.connectionStatusListeners.filter(l => l !== listener)
        }
    }

    _updateConnectionStatus(status, error = null) {
        this.connectionStatus = status
        this.connectionError = error
        this.connectionStatusListeners.forEach(l => l(status, error))
    }

    async _getNegotiateUrl() {
        if (!this.negotiateEndpoint) {
            throw new Error('Negotiate endpoint is not configured. Please set REACT_APP_NEGOTIATE_ENDPOINT environment variable.')
        }
        logger.debug('[PubSubService] Fetching negotiate URL', { endpoint: this.negotiateEndpoint })
        const isCheckoutAssistant = window.location.pathname.includes('/checkout-assistant')
        const source = isCheckoutAssistant ? 'Checkout-Assistant' : 'Consumer'
        const url =
            this.negotiateEndpoint.includes('?') ?
                this.negotiateEndpoint + `&source=${source}`
            :   this.negotiateEndpoint + `?source=${source}`
        const res = await fetch(url)
        if (!res.ok) {
            throw new Error(`Failed to negotiate: ${res.status} ${res.statusText}`)
        }
        const data = await res.json()
        if (!data.url) {
            throw new Error('Negotiate response missing url field')
        }
        logger.debug('[PubSubService] Negotiate URL fetched successfully')
        return data.url
    }

    async connect(options = {}) {
        const { ignoreAutoReconnect = false } = options
        // If auto-reconnect is disabled, only allow connection if explicitly ignored
        // This ensures all reconnection mechanisms respect the isAutoReconnectEnabled flag
        if (!this.isAutoReconnectEnabled && !ignoreAutoReconnect) {
            logger.debug(
                '[PubSubService] Auto-reconnect is disabled, blocking connect() call. Use connect({ ignoreAutoReconnect: true }) to ignore auto-reconnect.'
            )
            return
        }

        // Prevent duplicate connection attempts (race condition protection)
        // Only check _isConnecting flag, not connectionStatus, because connectionStatus might be stale
        // after a disconnect() call that cancelled a previous attempt
        if (this._isConnecting) {
            logger.debug('[PubSubService] Connection already in progress, skipping duplicate connect() call', {
                _isConnecting: this._isConnecting,
                connectionStatus: this.connectionStatus,
            })
            return
        }

        // If connectionStatus is 'connecting' but _isConnecting is false, it means a previous attempt
        // was cancelled. Reset the status to allow a new connection attempt.
        if (this.connectionStatus === CONNECTION_STATUS.CONNECTING && !this._isConnecting) {
            logger.debug('[PubSubService] Resetting stale connecting status to allow new connection attempt')
            this._updateConnectionStatus(CONNECTION_STATUS.DISCONNECTED)
        }

        if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
            logger.debug('[PubSubService] Socket already exists and is connecting/connected, skipping connect() call', {
                readyState: this.socket.readyState,
            })
            return
        }

        // Set connecting flag before async operations to prevent race conditions
        this._isConnecting = true
        // Increment connect attempt ID to track this connection attempt
        this._connectAttemptId++
        const currentAttemptId = this._connectAttemptId
        this._updateConnectionStatus(CONNECTION_STATUS.CONNECTING)

        logger.debug('[PubSubService] Starting connection attempt', {
            attemptId: currentAttemptId,
            negotiateEndpoint: this.negotiateEndpoint,
        })

        try {
            const url = await this._getNegotiateUrl()

            logger.debug('[PubSubService] Negotiate URL received', {
                attemptId: currentAttemptId,
                currentConnectAttemptId: this._connectAttemptId,
                urlLength: url?.length,
            })

            // Check if this connection attempt was cancelled (disconnect was called during negotiate)
            if (this._connectAttemptId !== currentAttemptId) {
                logger.debug('[PubSubService] Connection attempt was cancelled during negotiate, aborting', {
                    attemptId: currentAttemptId,
                    currentConnectAttemptId: this._connectAttemptId,
                })
                this._isConnecting = false
                return
            }

            // Double-check we're still not connected (socket might have been created by another call)
            if (this.socket && (this.socket.readyState === WebSocket.CONNECTING || this.socket.readyState === WebSocket.OPEN)) {
                logger.debug('[PubSubService] Socket was created by another call during URL fetch, aborting')
                this._isConnecting = false
                return
            }

            this.socket = new WebSocket(url, WEB_PUBSUB_PROTOCOLS)
            this.socket.onopen = this._handleOpen.bind(this)
            this.socket.onmessage = this._handleMessage.bind(this)
            this.socket.onclose = this._handleClose.bind(this)
            this.socket.onerror = this._handleError.bind(this)
            // Note: Do not store WebSocket URL as it expires quickly (minutes)
            // Always fetch a new URL on each connection attempt
            this.reconnectAttempts = 0

            logger.debug('[PubSubService] WebSocket connection established', {
                attemptId: currentAttemptId,
                url: url.substring(0, 100) + '...', // Log first 100 chars for debugging
            })
        } catch (err) {
            logger.error('[PubSubService] Connection error', {
                attemptId: currentAttemptId,
                error: err,
                negotiateEndpoint: this.negotiateEndpoint,
            })
            this._isConnecting = false
            this._updateConnectionStatus(CONNECTION_STATUS.ERROR, err)
            // Only schedule reconnect if auto-reconnect is enabled
            if (this.isAutoReconnectEnabled) {
                this._scheduleReconnect()
            } else {
                logger.debug('[PubSubService] Auto-reconnect is disabled, not attempting reconnection after connection error')
            }
        }
    }

    /**
     * Disconnect WebSocket connection
     * @param {Object} options - Disconnect options
     * @param {boolean} options.clearDesiredGroups - Whether to clear desired groups (default: false)
     *   - true: Hard disconnect - clears desired groups, prevents reconnection (use for logout)
     *   - false: Soft disconnect - preserves desired groups, allows reconnection (use for temporary disconnection)
     */
    disconnect(options = {}) {
        const { clearDesiredGroups = false } = options

        // Increment connect attempt ID to cancel any ongoing connect() calls
        // This prevents race condition where disconnect() is called during negotiate await
        // Always increment to cancel any pending connect() calls, even if no socket exists yet
        this._connectAttemptId++

        if (!this.socket) {
            // Even if no socket exists, clear connecting flag and reset status
            // This handles the case where disconnect() is called during negotiate await
            this._isConnecting = false
            // Reset status if it's still in 'connecting' state
            if (this.connectionStatus === CONNECTION_STATUS.CONNECTING) {
                this._updateConnectionStatus(CONNECTION_STATUS.DISCONNECTED)
            }
            // Clear desired groups if hard disconnect is requested
            if (clearDesiredGroups) {
                this._desiredGroups.clear()
            }
            return
        }

        if (this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout)
            this.reconnectTimeout = null
        }

        // Clear connecting flag
        this._isConnecting = false

        // Clear desired groups only if hard disconnect is requested
        // Soft disconnect preserves desired groups to allow automatic reconnection
        if (clearDesiredGroups) {
            this._desiredGroups.clear()
            logger.debug('[PubSubService] Hard disconnect: cleared desired groups')
        } else {
            logger.debug('[PubSubService] Soft disconnect: preserving desired groups for reconnection')
        }

        // Remove event listeners BEFORE closing to prevent processing messages during close
        // This simulates real network disconnection where no messages are received after disconnection
        this.socket.onmessage = null
        this.socket.onerror = null
        this.socket.onopen = null
        this.socket.onclose = null

        this.socket.close(1000, 'Manual disconnect')
        this.socket = null
        this._updateConnectionStatus(CONNECTION_STATUS.DISCONNECTED)

        // Clear joined groups on disconnect and notify
        if (this._groups.size) {
            this._groups.clear()
            this._notifyGroupStatus()
        }
    }

    _scheduleReconnect() {
        // Skip reconnection if auto-reconnect is disabled
        if (!this.isAutoReconnectEnabled) {
            logger.debug('[PubSubService] Auto-reconnect is disabled, skipping reconnection')
            return
        }

        if (this.reconnectAttempts >= this.maxReconnectAttempts) return
        this.reconnectAttempts++
        const delay = this.reconnectInterval * Math.pow(1.5, this.reconnectAttempts - 1)
        this.reconnectTimeout = setTimeout(() => this.connect(), delay)
    }

    _handleOpen() {
        this._isConnecting = false
        this._updateConnectionStatus(CONNECTION_STATUS.CONNECTED)
        this.reconnectAttempts = 0

        // Show success toast if:
        // 1. This is a reconnection (has desired groups to rejoin), OR
        // 2. This is initial connection but there's a stored group (CheckoutAssistant page)
        // Use module-level debounce to prevent duplicate toasts
        const hasDesiredGroups = this._desiredGroups.size > 0
        const hasStoredGroup = storageHelpers.getCheckoutAssistantCashRegisterId?.() != null

        if (hasDesiredGroups || hasStoredGroup) {
            const now = Date.now()
            // Only show toast if we haven't shown one in the last 2 seconds
            // This prevents duplicates if _handleOpen is called multiple times
            if (now - lastConnectionToastTime > CONNECTION_TOAST_DEBOUNCE_MS) {
                lastConnectionToastTime = now
                customToast.success('Connected to Checkout Assistant', { duration: 2000 })
            }
        }

        // Attempt to rejoin desired groups after reconnect
        // Use a small delay to ensure socket is fully ready before sending joinGroup messages
        if (this._desiredGroups.size) {
            const groupsToRejoin = Array.from(this._desiredGroups)
            // Wait a bit to ensure socket is fully ready, then rejoin sequentially
            setTimeout(() => {
                // Double-check socket is ready before attempting to rejoin
                if (this.socket && this.socket.readyState === WebSocket.OPEN) {
                    groupsToRejoin.forEach(group => {
                        try {
                            this.joinGroup(group)
                        } catch (error) {
                            // Log error but don't throw - allow other groups to rejoin
                            logger.error(`[PubSubService] Failed to rejoin group ${group}:`, error)
                            ErrorTrackingAPI.sendError(error, 'PubSubService', '_handleOpen', 'rejoinGroup')
                        }
                    })
                } else {
                    logger.warn('[PubSubService] Socket not ready when attempting to rejoin groups')
                }
            }, 100)
        }
    }

    _handleMessage(event) {
        // Guard: ignore messages if socket is null or event is from a different socket
        if (!this.socket) {
            logger.debug('[PubSubService] Ignoring message: socket is null')
            return
        }

        if (event.target !== this.socket) {
            logger.debug('[PubSubService] Ignoring message: event.target does not match current socket')
            return
        }

        // Convert readyState to readable string
        const readyStateMap = {
            [WebSocket.CONNECTING]: 'CONNECTING',
            [WebSocket.OPEN]: 'OPEN',
            [WebSocket.CLOSING]: 'CLOSING',
            [WebSocket.CLOSED]: 'CLOSED',
        }
        const readyStateText = readyStateMap[this.socket.readyState] || `UNKNOWN(${this.socket.readyState})`

        logger.debug('[PubSubService] Received message', {
            readyState: readyStateText,
            connectionStatus: this.connectionStatus,
        })

        try {
            const data = JSON.parse(event.data)

            this.listeners.forEach(l => {
                if (l.type === data.type || l.type === '*') l.callback(data)
            })
        } catch (e) {
            ErrorTrackingAPI.sendError(e, 'PubSubService', '_handleMessage', 'parseError')
        }
    }

    _handleClose(event) {
        logger.debug(`[PubSubService] WebSocket closed: code=${event?.code}, reason=${event?.reason || 'none'}`)

        // CRITICAL: Remove onmessage listener and clear socket reference FIRST
        // This ensures _handleMessage will immediately reject any queued messages
        // In real network disconnection, the browser won't fire onmessage events after disconnect,
        // but we need to handle edge cases where messages might be queued before close event
        // Note: socket reference may already be null (e.g., in testReconnection), so we use event.target
        const closedSocket = this.socket || (event && event.target)
        if (closedSocket) {
            // Remove onmessage FIRST to prevent any queued messages from being processed
            closedSocket.onmessage = null
            closedSocket.onerror = null
            closedSocket.onopen = null
            closedSocket.onclose = null
        }

        // Clear socket reference immediately so _handleMessage checks will fail
        // This must happen before updating status to ensure no messages are processed
        this.socket = null

        // Clear connecting flag
        this._isConnecting = false

        // Update status after removing listeners and clearing socket
        // This simulates real network disconnection where no messages are received after disconnection
        // After reconnection, the app should re-fetch data to sync state
        this._updateConnectionStatus(CONNECTION_STATUS.DISCONNECTED)

        // Clear joined groups on disconnect (desired groups are kept for potential reconnection if auto-reconnect is enabled)
        // This ensures UI correctly shows disconnected state
        if (this._groups.size) {
            this._groups.clear()
            this._notifyGroupStatus()
        }

        // Check if auto-reconnect is enabled first
        if (!this.isAutoReconnectEnabled) {
            logger.debug('[PubSubService] Auto-reconnect is disabled, not attempting reconnection')
            return
        }

        // Check if we should reconnect
        // Reconnect if:
        // 1. Close code is not 1000 (abnormal closure) - always reconnect
        // 2. Close code is 1000 but we have desired groups (was connected and should stay connected)
        //    This handles cases where browser/server converts abnormal disconnection to normal closure
        const shouldReconnect =
            (event && event.code !== 1000) || // Abnormal closure - always reconnect
            (event && event.code === 1000 && this._desiredGroups.size > 0) // Normal closure but we should be connected

        if (shouldReconnect) {
            logger.debug(
                `[PubSubService] Scheduling reconnection (code=${event?.code}, hasDesiredGroups=${this._desiredGroups.size > 0})`
            )
            this._scheduleReconnect()
        } else {
            logger.debug(`[PubSubService] Normal closure (code=${event?.code}) with no desired groups, not reconnecting`)
        }
    }

    _handleError() {
        this._isConnecting = false
        this._updateConnectionStatus(CONNECTION_STATUS.ERROR, new Error('WebSocket connection error'))
        // Trigger reconnection on error to ensure automatic recovery (if enabled)
        if (this.isAutoReconnectEnabled) {
            this._scheduleReconnect()
        } else {
            logger.debug('[PubSubService] Auto-reconnect is disabled, not attempting reconnection after error')
        }
    }

    addEventListener(type, callback) {
        const listener = { type, callback }
        this.listeners.push(listener)
        return () => {
            this.listeners = this.listeners.filter(l => l !== listener)
        }
    }

    // ----- Group status helpers -----
    _notifyGroupStatus() {
        const groups = Array.from(this._groups)
        this._groupStatusListeners.forEach(fn => fn(groups))
    }

    addGroupStatusListener(listener) {
        this._groupStatusListeners.push(listener)
        // Immediately emit current state
        listener(Array.from(this._groups))
        return () => {
            this._groupStatusListeners = this._groupStatusListeners.filter(l => l !== listener)
        }
    }

    getGroups() {
        return Array.from(this._groups)
    }

    isInGroup(group) {
        return this._groups.has(group)
    }

    /**
     * Enable or disable automatic reconnection
     * Useful for testing scenarios where you want manual control over reconnection
     * @param {boolean} enabled - Whether to enable auto-reconnect (default: true)
     */
    setAutoReconnectEnabled(enabled) {
        this.isAutoReconnectEnabled = enabled
        logger.debug(`[PubSubService] Auto-reconnect ${enabled ? 'enabled' : 'disabled'}`)

        // If disabling, clear any pending reconnection
        if (!enabled && this.reconnectTimeout) {
            clearTimeout(this.reconnectTimeout)
            this.reconnectTimeout = null
            logger.debug('[PubSubService] Cleared pending reconnection timeout')
        }
    }

    /**
     * Get current auto-reconnect status
     * @returns {boolean} Whether auto-reconnect is enabled
     */
    getAutoReconnectEnabled() {
        return this.isAutoReconnectEnabled
    }

    /**
     * Internal send helper
     * @param {Object} payload - JSON message to send
     * @returns {void}
     * @private
     */
    _send(payload) {
        if (!this.socket || this.socket.readyState !== WebSocket.OPEN) {
            const err = new Error('WebSocket is not connected')
            throw err
        }

        this.socket.send(JSON.stringify(payload))
    }

    /**
     * Wait until the WebSocket connection is established or error/timeout occurs
     * Verifies both connectionStatus and actual socket readyState
     * @param {number} timeoutMs default 10000ms
     * @returns {Promise<void>}
     */
    waitUntilConnected(timeoutMs = 10000) {
        // Check if already connected (both status and socket readyState)
        if (this.connectionStatus === CONNECTION_STATUS.CONNECTED && this.socket && this.socket.readyState === WebSocket.OPEN) {
            return Promise.resolve()
        }
        if (this.connectionStatus === CONNECTION_STATUS.ERROR)
            return Promise.reject(this.connectionError || new Error('connection error'))

        return new Promise((resolve, reject) => {
            const start = Date.now()
            let isResolved = false

            const checkSocketReady = () => {
                if (isResolved) return
                // Verify both connectionStatus and actual socket readyState
                if (
                    this.connectionStatus === CONNECTION_STATUS.CONNECTED &&
                    this.socket &&
                    this.socket.readyState === WebSocket.OPEN
                ) {
                    isResolved = true
                    off()
                    resolve()
                    return true
                }
                return false
            }

            const off = this.addConnectionStatusListener((s, e) => {
                if (isResolved) return

                if (s === CONNECTION_STATUS.CONNECTED) {
                    // When status becomes 'connected', verify socket is actually ready
                    // Use a small delay to ensure socket.readyState is updated after onopen event
                    setTimeout(() => {
                        if (checkSocketReady()) return
                        // If still not ready after delay, check timeout
                        if (Date.now() - start > timeoutMs) {
                            isResolved = true
                            off()
                            reject(new Error('Timed out waiting for socket to be ready'))
                        }
                    }, 100)
                } else if (s === CONNECTION_STATUS.ERROR) {
                    isResolved = true
                    off()
                    reject(e || new Error('connection error'))
                } else if (Date.now() - start > timeoutMs) {
                    isResolved = true
                    off()
                    reject(new Error('Timed out waiting for connection'))
                }
            })

            // Also check immediately in case connectionStatus is already 'connected'
            checkSocketReady()
        })
    }

    /**
     * Join an Azure Web PubSub group
     * @param {string} group - Group name
     * @returns {void}
     */
    joinGroup(group) {
        const payload = { type: 'joinGroup', group }
        this._send(payload)
        this._desiredGroups.add(group)

        // Update local group state immediately (optimistic update)
        if (!this._groups.has(group)) {
            this._groups.add(group)
            this._notifyGroupStatus()
        }
    }

    /**
     * Leave an Azure Web PubSub group
     * @param {string} group - Group name
     * @returns {void}
     */
    leaveGroup(group) {
        const payload = { type: 'leaveGroup', group }
        this._send(payload)
        if (this._desiredGroups.has(group)) this._desiredGroups.delete(group)

        // Update local group state immediately (optimistic update)
        if (this._groups.has(group)) {
            this._groups.delete(group)
            this._notifyGroupStatus()
        }
    }

    /**
     * Send a message to an Azure Web PubSub group
     * @param {string} group - Group name
     * @param {any} data - Data to send (string or JSON-serializable)
     * @param {Object} [options]
     * @param {'text'|'json'|'binary'} [options.dataType] - Defaults inferred from data
     * @param {boolean} [options.noEcho] - Avoid echoing to sender
     * @returns {void}
     */
    sendToGroup(group, data, options = {}) {
        const { dataType, noEcho } = options
        let resolvedType = dataType
        let payloadData = data

        if (!resolvedType) {
            if (typeof data === 'string') {
                resolvedType = 'text'
            } else if (data instanceof Uint8Array) {
                resolvedType = 'binary'
            } else {
                resolvedType = 'json'
            }
        }

        if (resolvedType === 'json' && typeof data !== 'string') {
            // Allow objects, arrays, numbers, etc.
            payloadData = data
        }

        const payload = {
            type: 'sendToGroup',
            group,
            data: payloadData,
            dataType: resolvedType,
            ...(typeof noEcho === 'boolean' ? { noEcho } : {}),
        }

        this._send(payload)
    }
}

export const usePubSub = (options = {}) => {
    const { groupName } = options || {}
    const [connectionStatus, setConnectionStatus] = useState(pubSubService.getConnectionStatus())
    const [connectionError, setConnectionError] = useState(pubSubService.getConnectionError())
    const [groups, setGroups] = useState(pubSubService.getGroups())

    useEffect(() => {
        const removeListener = pubSubService.addConnectionStatusListener((s, e) => {
            setConnectionStatus(s)
            setConnectionError(e)
        })
        const removeGroups = pubSubService.addGroupStatusListener(next => setGroups(next))

        // Clean up listener on unmount
        return () => {
            removeListener()
            removeGroups()
        }
    }, [])

    const connect = useCallback(() => pubSubService.connect(), [])
    const waitUntilConnected = useCallback(timeoutMs => pubSubService.waitUntilConnected(timeoutMs), [])
    const disconnect = useCallback(options => pubSubService.disconnect(options), [])
    const addEventListener = useCallback((type, cb) => pubSubService.addEventListener(type, cb), [])

    // Group operations
    const joinGroup = useCallback(
        (group, opts) => {
            const g = group ?? groupName
            if (!g) throw new Error('joinGroup requires a group name (provide arg or options.groupName)')
            return pubSubService.joinGroup(g, opts)
        },
        [groupName]
    )

    const leaveGroup = useCallback(
        (group, opts) => {
            const g = group ?? groupName
            if (!g) throw new Error('leaveGroup requires a group name (provide arg or options.groupName)')
            return pubSubService.leaveGroup(g, opts)
        },
        [groupName]
    )

    const sendToGroup = useCallback(
        (group, data, opts) => {
            const g = group ?? groupName
            if (!g) throw new Error('sendToGroup requires a group name (provide arg or options.groupName)')
            return pubSubService.sendToGroup(g, data, opts)
        },
        [groupName]
    )

    const isInGroup = useCallback(group => pubSubService.isInGroup(group), [])

    return {
        connectionStatus,
        connectionError,
        connect,
        waitUntilConnected,
        disconnect,
        addEventListener,
        isConnected: connectionStatus === CONNECTION_STATUS.CONNECTED,
        isConnecting: connectionStatus === CONNECTION_STATUS.CONNECTING,
        hasError: connectionStatus === CONNECTION_STATUS.ERROR,
        // group helpers
        joinGroup,
        leaveGroup,
        sendToGroup,
        isInGroup,
        groups,
    }
}

/**
 * PubSubInitializer component
 * Initializes the PubSub event handler with Redux dispatch
 * Use this component inside a Redux Provider
 */
export const PubSubInitializer = ({ children }) => {
    const dispatch = useDispatch()

    useEffect(() => {
        // Initialize the PubSub event handler with dispatch
        if (pubSubEventHandler) {
            pubSubEventHandler.initialize(dispatch)
        }

        // Bridge PubSubService connection status to Redux pubsub slice
        const removeStatusListener = pubSubService.addConnectionStatusListener((status, error) => {
            dispatch(pubsubActions.setStatus(status))
            if (error) dispatch(pubsubActions.setError(error.message || String(error)))
        })

        // Auto-connect WebSocket on app init (use ignoreAutoReconnect=true to allow initial connection even if auto-reconnect is disabled)
        pubSubService.connect({ ignoreAutoReconnect: true })

        // Clean up when the component is unmounted
        return () => {
            if (pubSubEventHandler) {
                pubSubEventHandler.cleanup()
            }
            if (removeStatusListener) removeStatusListener()
            // Hard disconnect on unmount to prevent reconnection after app cleanup
            pubSubService.disconnect({ clearDesiredGroups: true })
        }
    }, [dispatch])

    // Return children if provided, otherwise null
    return children || null
}

const pubSubService = new PubSubService()
export default pubSubService
